import React, { useEffect, useState } from "react";

const DEFAULT_TARGET_DATE = "2026-06-28T11:00:00+05:30";
const SEMINAR_ID = "neet-ug-2026-summit";

const DEFAULT_CONFIG = {
  date: "28 June 2026",
  time: "11:00 AM IST",
  price: 0,
  registered: 10500,
};

function normalizeDateText(dateText) {
  if (!dateText) return "";

  const value = String(dateText).trim();

  // Format: 2026-06-28
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // Format: 28-06-2026 or 28/06/2026
  const numericMatch = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);

  if (numericMatch) {
    const [, day, month, year] = numericMatch;

    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
      2,
      "0"
    )}`;
  }

  // Format: 28 June 2026
  return value;
}

function parseSeminarDateTime(date, time) {
  if (!date || !time) {
    return new Date(DEFAULT_TARGET_DATE).getTime();
  }

  const normalizedDate = normalizeDateText(date);

  const cleanTime = String(time)
    .replace(/\s*IST\s*$/i, "")
    .trim();

  const parsedDate = new Date(`${normalizedDate} ${cleanTime} GMT+0530`);

  if (Number.isNaN(parsedDate.getTime())) {
    return new Date(DEFAULT_TARGET_DATE).getTime();
  }

  return parsedDate.getTime();
}

function formatDisplayDate(dateText) {
  if (!dateText) return "TBA";

  const normalizedDate = normalizeDateText(dateText);
  const parsedDate = new Date(`${normalizedDate} GMT+0530`);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateText;
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDisplayTime(timeText) {
  if (!timeText) return "TBA";

  return String(timeText).replace(/\s*IST\s*$/i, "").trim();
}

function getSeminarConfigUrl() {
  const baseUrl = process.env.NEXT_PUBLIC_ADMIN_URL || "";

  return `${baseUrl.replace(/\/+$/, "")}/api/seminar-config/${SEMINAR_ID}`;
}

export default function SeminarFooterRegisterBar({
  onRegister,
  price: fallbackPrice = 0,
  registered: fallbackRegistered = 10500,
}) {
  const [config, setConfig] = useState({
    ...DEFAULT_CONFIG,
    price: fallbackPrice,
    registered: fallbackRegistered,
  });

  const [targetDate, setTargetDate] = useState(
    new Date(DEFAULT_TARGET_DATE).getTime()
  );

  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchSeminarConfig() {
      try {
        const apiUrl = getSeminarConfigUrl();

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store",
        });

        const json = await response.json();

        if (!response.ok) {
          console.error(
            "Seminar footer config API error:",
            response.status,
            json
          );

          setTargetDate(
            parseSeminarDateTime(DEFAULT_CONFIG.date, DEFAULT_CONFIG.time)
          );

          return;
        }

        const cfg = json?.data || json;

        if (!isMounted) return;

        const dynamicDate = cfg?.date || DEFAULT_CONFIG.date;
        const dynamicTime = cfg?.time || DEFAULT_CONFIG.time;

        const dynamicPrice =
          cfg?.price !== undefined && cfg?.price !== null
            ? Number(cfg.price)
            : fallbackPrice;

        const dynamicRegistered =
          cfg?.registered !== undefined && cfg?.registered !== null
            ? Number(cfg.registered)
            : fallbackRegistered;

        setConfig({
          date: dynamicDate,
          time: dynamicTime,
          price: dynamicPrice,
          registered: dynamicRegistered,
        });

        setTargetDate(parseSeminarDateTime(dynamicDate, dynamicTime));
      } catch (error) {
        console.error("Seminar footer config fetch error:", error);

        setTargetDate(
          parseSeminarDateTime(DEFAULT_CONFIG.date, DEFAULT_CONFIG.time)
        );
      }
    }

    fetchSeminarConfig();

    return () => {
      isMounted = false;
    };
  }, [fallbackPrice, fallbackRegistered]);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const diff = targetDate - now;

      if (diff <= 0) {
        setIsExpired(true);

        setTimeLeft({
          days: "00",
          hours: "00",
          minutes: "00",
          seconds: "00",
        });

        return;
      }

      setIsExpired(false);

      const days = String(
        Math.floor(diff / (1000 * 60 * 60 * 24))
      ).padStart(2, "0");

      const hours = String(
        Math.floor((diff / (1000 * 60 * 60)) % 24)
      ).padStart(2, "0");

      const minutes = String(
        Math.floor((diff / (1000 * 60)) % 60)
      ).padStart(2, "0");

      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(
        2,
        "0"
      );

      setTimeLeft({
        days,
        hours,
        minutes,
        seconds,
      });
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  const priceLabel = config.price === 0 ? "FREE" : `₹${config.price}`;

  const formatCount = (count) => {
    if (count == null) return "—";
    if (count >= 100000) return `${(count / 100000).toFixed(1)}L+`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k+`;
    return `${count}`;
  };

  if (isExpired) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] border-t border-orange-100 bg-[#FFF2ED] shadow-[0_-4px_18px_rgba(0,0,0,0.12)]">
      <style jsx>{`
        @keyframes blinkDot {
          0% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
          }

          50% {
            opacity: 0.4;
            transform: scale(0.85);
            box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
          }

          100% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
          }
        }

        .blink-dot {
          animation: blinkDot 1s infinite;
          will-change: opacity, transform, box-shadow;
        }

        @media (max-width: 420px) {
          .mobile-hide-price-text {
            display: none;
          }

          .mobile-timer {
            padding-left: 6px;
            padding-right: 6px;
          }

          .mobile-register-btn {
            padding-left: 10px;
            padding-right: 10px;
          }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-2 py-2 sm:py-3">
          {/* Price */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <span className="mobile-hide-price-text hidden text-xs font-bold text-gray-600 sm:inline">
              Register for
            </span>

            <span className="text-[9px] font-bold text-gray-400 line-through sm:text-xs">
              ₹499
            </span>

            <span className="rounded-full bg-[#ff9245] px-2 py-0.5 text-[10px] font-bold text-white sm:px-3 sm:py-1 sm:text-sm">
              {priceLabel}
            </span>
          </div>

          {/* Date / Time - Desktop */}
          <div className="hidden shrink-0 items-center gap-2 md:flex">
            <div className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#0E605A] shadow-sm">
              {formatDisplayDate(config.date)}
            </div>

            <div className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#0E605A] shadow-sm">
              {formatDisplayTime(config.time)}
            </div>
          </div>

          {/* Timer */}
          <div className="mobile-timer flex shrink-0 items-center gap-1 rounded-full bg-white px-2 py-1 shadow-sm sm:gap-2 sm:px-4">
            <div className="hidden text-[9px] font-bold text-[#0E605A] sm:block sm:text-xs">
              Ends In
            </div>

            <div className="flex items-center gap-1 text-[10px] font-extrabold text-[#ff9245] sm:text-sm">
              <span>{timeLeft.days}d</span>
              <span>{timeLeft.hours}h</span>
              <span>{timeLeft.minutes}m</span>
              <span>{timeLeft.seconds}s</span>
            </div>
          </div>

          {/* Registered Count */}
          <div className="hidden shrink-0 items-center gap-1 sm:flex">
            <span
              className="blink-dot inline-block h-3 w-3 rounded-full bg-green-500"
              aria-hidden="true"
            />

            <div className="leading-tight">
              <div className="text-sm font-bold text-[#0E605A]">
                {formatCount(config.registered)}
              </div>

              <div className="text-[10px] text-gray-500">Registered</div>
            </div>
          </div>

          {/* Button */}
          <button
            type="button"
            onClick={onRegister}
            className="mobile-register-btn shrink-0 rounded-full bg-[#ff9245] px-3 py-2 text-[11px] font-bold text-white shadow-lg transition hover:bg-[#ff9245]/90 sm:px-6 sm:py-2.5 sm:text-base"
          >
            Register Now
          </button>
        </div>
      </div>
    </div>
  );
}